/** Compliance — my violations, HR review queue, all violations, variance report, rules. */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import useWorkspacePerms from "@/lib/useWorkspacePerms";
import PageHeader from "@/components/PageHeader";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { ShieldAlert, ClipboardCheck, BarChart3, Settings2, CheckCircle2, XCircle, PlayCircle } from "lucide-react";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { PersonChip } from "@/components/PersonChip";
import AdvancedTable from "@/components/table/AdvancedTable";

const iso = (d) => d.toISOString().slice(0, 10);
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return iso(d); };

export default function Compliance() {
  const { activeWorkspaceId: wsId } = useAuth();
  const { can, ready } = useWorkspacePerms();
  const isHr = ready && (can("hr.approve") || can("hr.manage"));
  const canManage = ready && can("hr.manage");
  return (
    <>
      <PageHeader eyebrow="HRMS" title="Compliance" description="Timesheet discipline rules, violations, HR point reviews and variance." testid="compliance-header" />
      <div className="px-4 sm:px-6 md:px-10 pb-10">
        <Tabs defaultValue="mine">
          <TabsList className="rounded-none flex-wrap h-auto">
            <TabsTrigger value="mine" className="rounded-none" data-testid="comp-tab-mine"><ShieldAlert className="w-4 h-4 mr-2" />My compliance</TabsTrigger>
            {isHr && <TabsTrigger value="queue" className="rounded-none" data-testid="comp-tab-queue"><ClipboardCheck className="w-4 h-4 mr-2" />Review queue</TabsTrigger>}
            {isHr && <TabsTrigger value="variance" className="rounded-none" data-testid="comp-tab-variance"><BarChart3 className="w-4 h-4 mr-2" />Variance</TabsTrigger>}
            {canManage && <TabsTrigger value="rules" className="rounded-none" data-testid="comp-tab-rules"><Settings2 className="w-4 h-4 mr-2" />Rules</TabsTrigger>}
          </TabsList>
          <TabsContent value="mine" className="mt-5"><MineTab wsId={wsId} isHr={isHr} /></TabsContent>
          {isHr && <TabsContent value="queue" className="mt-5"><QueueTab wsId={wsId} /></TabsContent>}
          {isHr && <TabsContent value="variance" className="mt-5"><VarianceTab wsId={wsId} /></TabsContent>}
          {canManage && <TabsContent value="rules" className="mt-5"><RulesTab wsId={wsId} /></TabsContent>}
        </Tabs>
      </div>
    </>
  );
}

function StatusBadge({ status }) {
  const cls = status === "approved" ? "bg-emerald-500/15 text-emerald-600" :
    status === "rejected" ? "bg-red-500/15 text-red-600" : "bg-amber-500/15 text-amber-600";
  return <Badge variant="outline" className={`rounded-none border-0 ${cls}`}>{status}</Badge>;
}

function MineTab({ wsId, isHr }) {
  const [viols, setViols] = useState([]);
  const [adjs, setAdjs] = useState([]);
  const load = () => {
    api.get(`/workspaces/${wsId}/compliance/violations`).then((r) => setViols(r.data)).catch(() => {});
    api.get(`/workspaces/${wsId}/compliance/review-queue?status=all`).then((r) => setAdjs(r.data)).catch(() => {});
  };
  useEffect(load, [wsId]);
  return (
    <div className="grid md:grid-cols-2 gap-6">
      <div>
        <h3 className="text-sm font-semibold mb-3">Violations (latest)</h3>
        <div className="space-y-2" data-testid="my-violations-list">
          {viols.length === 0 && <p className="text-sm text-muted-foreground" data-testid="my-violations-empty">No violations. Keep it up! 🎉</p>}
          {viols.slice(0, 50).map((v) => (
            <div key={v.id} className="border p-3 flex items-start justify-between gap-3" data-testid={`violation-${v.id}`}>
              <div>
                <p className="text-sm font-medium">{v.rule_label}{isHr && v.user_name ? <> — <PersonChip userId={v.user_id} name={v.user_name} /></> : null}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{v.detail}</p>
              </div>
              <div className="text-right shrink-0">
                <p className="text-xs font-mono">{v.date}</p>
                {v.escalated && <Badge variant="outline" className="rounded-none border-0 bg-red-500/15 text-red-600 mt-1">escalated</Badge>}
              </div>
            </div>
          ))}
        </div>
      </div>
      <div>
        <h3 className="text-sm font-semibold mb-3">Point adjustments</h3>
        <div className="space-y-2" data-testid="my-adjustments-list">
          {adjs.length === 0 && <p className="text-sm text-muted-foreground">Nothing queued yet.</p>}
          {adjs.slice(0, 50).map((a) => (
            <div key={a.id} className="border p-3 flex items-start justify-between gap-3" data-testid={`adjustment-${a.id}`}>
              <div>
                <p className={`text-sm font-semibold ${a.points >= 0 ? "text-emerald-600" : "text-red-600"}`}>{a.points >= 0 ? "+" : ""}{a.points} pts{a.user_name ? ` — ${a.user_name}` : ""}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{a.reason}</p>
              </div>
              <div className="text-right shrink-0">
                <p className="text-xs font-mono">{a.date}</p>
                <StatusBadge status={a.status} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function QueueTab({ wsId }) {
  const [rows, setRows] = useState([]);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [sweepDate, setSweepDate] = useState(daysAgo(1));
  const [busy, setBusy] = useState(true);
  const load = () => {
    setBusy(true);
    api.get(`/workspaces/${wsId}/compliance/review-queue`)
      .then((r) => setRows(r.data))
      .catch(() => {})
      .finally(() => setBusy(false));
  };
  useEffect(() => { load(); }, [wsId]);

  const review = async (aid, approve) => {
    try {
      await api.post(`/compliance-adjustments/${aid}/review`, { approve });
      toast.success(approve ? "Approved — points applied" : "Rejected");
      load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };
  const bulk = async (approve) => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    try {
      const { data } = await api.post(`/workspaces/${wsId}/compliance/review-queue/bulk`, { ids, approve });
      toast.success(`${data.reviewed} reviewed${data.skipped ? `, ${data.skipped} skipped` : ""}`);
      setSelectedIds(new Set()); load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };
  const runSweep = async () => {
    try {
      const { data } = await api.post(`/workspaces/${wsId}/compliance/run-sweep?date=${sweepDate}`);
      toast.success(`Sweep done — ${data.evaluated ?? 0} evaluated, ${data.violations ?? 0} violations`);
      load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const columns = useMemo(() => [
    {
      key: "user_name", label: "Member", width: 220, sortable: true, filterType: "text",
      accessor: (a) => a.user_name || "",
      render: (a) => (
        <div className="flex items-center gap-2 min-w-0">
          <PersonChip userId={a.user_id} name={a.user_name} />
        </div>
      ),
    },
    {
      key: "points", label: "Points", width: 90, sortable: true, align: "right",
      filterType: "number-range",
      accessor: (a) => a.points,
      render: (a) => <span className={`font-mono text-xs ${a.points >= 0 ? "text-emerald-600" : "text-red-600"}`}>{a.points >= 0 ? "+" : ""}{a.points}</span>,
    },
    {
      key: "reason", label: "Reason", width: 340, sortable: true, filterType: "text",
      accessor: (a) => a.reason || "",
      render: (a) => <span className="text-xs text-muted-foreground truncate block">{a.reason || "—"}</span>,
    },
    {
      key: "date", label: "Date", width: 110, sortable: true, filterType: "date-range",
      accessor: (a) => a.date || "",
      render: (a) => <span className="text-xs font-mono">{a.date}</span>,
    },
  ], []);

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="ml-auto flex items-center gap-2">
          <Input type="date" value={sweepDate} onChange={(e) => setSweepDate(e.target.value)} className="rounded-none w-40" data-testid="sweep-date-input" />
          <Button size="sm" variant="secondary" className="rounded-none" onClick={runSweep} data-testid="run-sweep-btn"><PlayCircle className="w-4 h-4 mr-1" />Run sweep</Button>
        </div>
      </div>
      <div data-testid="review-queue-list">
        {rows.length === 0 && !busy && (
          <p className="text-sm text-muted-foreground mb-2" data-testid="review-queue-empty">Queue is empty — nothing pending HR review.</p>
        )}
        <AdvancedTable
          tableKey="compliance-review-queue"
          title="Review queue"
          rows={rows}
          columns={columns}
          loading={busy && rows.length === 0}
          emptyIcon={ClipboardCheck}
          emptyTitle="Queue is empty"
          emptyDescription="No pending HR reviews right now."
          selectable
          selectedIds={selectedIds}
          onSelectionChange={setSelectedIds}
          renderBulkBar={() => (
            <>
              <Button size="sm" className="rounded-none h-7 text-xs" onClick={() => bulk(true)} data-testid="bulk-approve-btn">
                <CheckCircle2 className="w-3.5 h-3.5 mr-1" />Approve selected
              </Button>
              <Button size="sm" variant="outline" className="rounded-none h-7 text-xs" onClick={() => bulk(false)} data-testid="bulk-reject-btn">
                <XCircle className="w-3.5 h-3.5 mr-1" />Reject selected
              </Button>
            </>
          )}
          renderRowActions={(a) => (
            <div className="flex gap-1 justify-end">
              <Button size="sm" variant="ghost" className="rounded-none h-7 px-2" onClick={() => review(a.id, true)} data-testid={`approve-${a.id}`}><CheckCircle2 className="w-3.5 h-3.5" /></Button>
              <Button size="sm" variant="ghost" className="rounded-none h-7 px-2" onClick={() => review(a.id, false)} data-testid={`reject-${a.id}`}><XCircle className="w-3.5 h-3.5" /></Button>
            </div>
          )}
        />
      </div>
    </div>
  );
}

function VarianceTab({ wsId }) {
  const [from, setFrom] = useState(daysAgo(14));
  const [to, setTo] = useState(daysAgo(0));
  const [data, setData] = useState(null);
  const load = () => api.get(`/workspaces/${wsId}/compliance/variance?from=${from}&to=${to}`).then((r) => setData(r.data)).catch((e) => toast.error(apiErrorMessage(e)));
  useEffect(() => { load(); }, [wsId]);
  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="rounded-none w-40" data-testid="variance-from" />
        <span className="text-sm text-muted-foreground">→</span>
        <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="rounded-none w-40" data-testid="variance-to" />
        <Button size="sm" className="rounded-none" onClick={load} data-testid="variance-load-btn">Apply</Button>
      </div>
      <div className="overflow-x-auto border">
        <table className="w-full text-sm" data-testid="variance-table">
          <thead className="bg-muted/50 text-left">
            <tr>
              <th className="p-2.5 font-medium">Member</th>
              <th className="p-2.5 font-medium text-right">Planned h</th>
              <th className="p-2.5 font-medium text-right">Actual h</th>
              <th className="p-2.5 font-medium text-right">Variance</th>
              <th className="p-2.5 font-medium text-right">Billable %</th>
              <th className="p-2.5 font-medium text-right">Violations</th>
            </tr>
          </thead>
          <tbody>
            {(data?.rows || []).map((r) => (
              <tr key={r.user_id} className="border-t" data-testid={`variance-row-${r.user_id}`}>
                <td className="p-2.5">{r.user_name}</td>
                <td className="p-2.5 text-right font-mono">{r.planned_hours}</td>
                <td className="p-2.5 text-right font-mono">{r.actual_hours}</td>
                <td className={`p-2.5 text-right font-mono ${r.variance_hours < 0 ? "text-red-600" : "text-emerald-600"}`}>{r.variance_hours >= 0 ? "+" : ""}{r.variance_hours}{r.variance_pct != null ? ` (${r.variance_pct}%)` : ""}</td>
                <td className="p-2.5 text-right font-mono">{r.billable_pct}%</td>
                <td className={`p-2.5 text-right font-mono ${r.violations ? "text-red-600 font-semibold" : ""}`}>{r.violations}</td>
              </tr>
            ))}
            {data && data.rows.length === 0 && <tr><td className="p-4 text-muted-foreground" colSpan={6}>No data in this range.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const RULE_FIELDS = [
  { k: "daily_timesheet_required", label: "Daily timesheet must be submitted", type: "bool", penalty: "penalty_missing_timesheet" },
  { k: "min_hours_per_day", label: "Minimum hours per working day", type: "num", penalty: "penalty_min_hours" },
  { k: "plan_before_work", label: "Daily plan required before logging work", type: "bool", penalty: "penalty_no_plan" },
  { k: "min_billable_pct", label: "Minimum billable % (0 = off)", type: "num", penalty: "penalty_billable_pct" },
  // Feb 2026 R117 · Attendance + deliverable rules
  { k: "check_in_required",  label: "Check-in required each working day", type: "bool", penalty: "penalty_missing_check_in" },
  { k: "check_out_required", label: "Check-out required each working day", type: "bool", penalty: "penalty_missing_check_out" },
  { k: "attendance_min_pct", label: "Minimum daily attendance % (0 = off)", type: "num", penalty: "penalty_attendance_pct" },
  { k: "deadline_check_enabled", label: "Task deadlines must not be missed", type: "bool", penalty: "penalty_missed_deadline" },
  { k: "goal_check_enabled",  label: "Goal deadlines must not be missed", type: "bool", penalty: "penalty_missed_goal" },
  // R268vv · Feb 2026 · Effort-exceeded as a first-class rule.
  { k: "effort_check_enabled", label: "Task effort must stay within estimate", type: "bool", penalty: "penalty_effort_exceeded" },
  { k: "effort_overshoot_threshold_pct", label: "Overshoot threshold % (fire when logged exceeds estimate by more than this)", type: "num" },
];

function RulesTab({ wsId }) {
  const [rules, setRules] = useState(null);
  // R119 · Preview state — {loading, data, err, date}
  const [preview, setPreview] = useState({ loading: false, data: null, err: null });
  useEffect(() => { api.get(`/workspaces/${wsId}/compliance/rules`).then((r) => setRules(r.data)).catch(() => {}); }, [wsId]);
  if (!rules) return null;
  const set = (k, v) => setRules((r) => ({ ...r, [k]: v }));
  const save = async () => {
    try {
      const { data } = await api.put(`/workspaces/${wsId}/compliance/rules`, rules);
      setRules(data); toast.success("Compliance rules saved");
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };
  const doPreview = async () => {
    setPreview({ loading: true, data: null, err: null });
    try {
      const yday = daysAgo(1);
      const { data } = await api.post(`/workspaces/${wsId}/compliance/preview?date=${yday}`, rules);
      setPreview({ loading: false, data, err: null });
    } catch (e) {
      setPreview({ loading: false, data: null, err: apiErrorMessage(e) });
    }
  };
  return (
    <div className="max-w-2xl space-y-5" data-testid="rules-form">
      <div className="flex items-center justify-between border p-3">
        <div>
          <p className="text-sm font-medium">Compliance engine enabled</p>
          <p className="text-xs text-muted-foreground">Daily sweep runs at 03:30 UTC and evaluates the previous day.</p>
        </div>
        <Switch checked={rules.enabled} onCheckedChange={(v) => set("enabled", v)} data-testid="rule-enabled-switch" />
      </div>
      {RULE_FIELDS.map((f) => (
        <div key={f.k} className="flex items-center justify-between border p-3 gap-4">
          <p className="text-sm">{f.label}</p>
          <div className="flex items-center gap-3 shrink-0">
            {f.type === "bool"
              ? <Switch checked={!!rules[f.k]} onCheckedChange={(v) => set(f.k, v)} data-testid={`rule-${f.k}`} />
              : <Input type="number" value={rules[f.k]} onChange={(e) => set(f.k, Number(e.target.value))} className="rounded-none w-24" data-testid={`rule-${f.k}`} />}
            <div className="flex items-center gap-1">
              <span className="text-xs text-muted-foreground">penalty</span>
              <Input type="number" value={rules[f.penalty]} onChange={(e) => set(f.penalty, Number(e.target.value))} className="rounded-none w-20" data-testid={`rule-${f.penalty}`} />
            </div>
          </div>
        </div>
      ))}
      <div className="grid grid-cols-2 gap-4">
        <label className="text-sm space-y-1"><span>Bonus per compliant day</span>
          <Input type="number" value={rules.bonus_compliant_day} onChange={(e) => set("bonus_compliant_day", Number(e.target.value))} className="rounded-none" data-testid="rule-bonus" /></label>
        <label className="text-sm space-y-1"><span>Escalate after N violations</span>
          <Input type="number" value={rules.escalation_threshold} onChange={(e) => set("escalation_threshold", Number(e.target.value))} className="rounded-none" data-testid="rule-escalation-threshold" /></label>
        <label className="text-sm space-y-1"><span>Escalation window (days)</span>
          <Input type="number" value={rules.escalation_window_days} onChange={(e) => set("escalation_window_days", Number(e.target.value))} className="rounded-none" data-testid="rule-escalation-window" /></label>
        <div className="flex items-end justify-between border p-3">
          <span className="text-sm">Email alerts</span>
          <Switch checked={rules.email_alerts} onCheckedChange={(v) => set("email_alerts", v)} data-testid="rule-email-alerts" />
        </div>
      </div>
      <div className="flex gap-2 flex-wrap">
        <Button className="rounded-none" onClick={save} data-testid="rules-save-btn">Save rules</Button>
        <Button variant="outline" className="rounded-none" onClick={doPreview} disabled={preview.loading} data-testid="rules-preview-btn">
          <PlayCircle className="w-4 h-4 mr-2" />
          {preview.loading ? "Previewing…" : "Preview yesterday"}
        </Button>
      </div>
      {preview.data && (
        <div className="border p-4 space-y-3 bg-muted/20" data-testid="rules-preview-panel">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold">Dry-run against {preview.data.date}</p>
              <p className="text-xs text-muted-foreground">Nothing was written to the ledger. Numbers reflect the rules currently on-screen.</p>
            </div>
            <button className="text-xs text-muted-foreground hover:text-foreground" onClick={() => setPreview({ loading: false, data: null, err: null })}>Close</button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
            <div className="border p-2"><p className="text-[10px] uppercase text-muted-foreground">Users evaluated</p><p className="font-mono font-bold text-base">{preview.data.users_evaluated}</p></div>
            <div className="border p-2"><p className="text-[10px] uppercase text-muted-foreground">Violations</p><p className="font-mono font-bold text-base">{preview.data.violations}</p></div>
            <div className="border p-2"><p className="text-[10px] uppercase text-muted-foreground">Would deduct</p><p className="font-mono font-bold text-base text-rose-700">{preview.data.would_deduct_pts}</p></div>
            <div className="border p-2"><p className="text-[10px] uppercase text-muted-foreground">Net (with bonuses)</p><p className={`font-mono font-bold text-base ${preview.data.net_pts >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{preview.data.net_pts >= 0 ? "+" : ""}{preview.data.net_pts}</p></div>
          </div>
          <div className="border">
            <p className="text-xs font-semibold px-2 py-1.5 bg-muted/40 border-b">Per rule</p>
            {Object.entries(preview.data.per_rule || {}).length === 0 ? (
              <p className="text-xs text-muted-foreground p-2">No violations. Everyone would earn the compliant-day bonus.</p>
            ) : (
              <table className="w-full text-xs">
                <tbody>
                  {Object.entries(preview.data.per_rule).map(([k, v]) => (
                    <tr key={k} className="border-b last:border-b-0">
                      <td className="p-2">{v.label}</td>
                      <td className="p-2 text-right tabular-nums">{v.count}×</td>
                      <td className="p-2 text-right tabular-nums text-rose-700">{v.points} pts</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          {(preview.data.per_user || []).length > 0 && (
            <details className="border">
              <summary className="text-xs font-semibold px-2 py-1.5 bg-muted/40 cursor-pointer">Top affected users ({preview.data.per_user.length})</summary>
              <table className="w-full text-xs">
                <tbody>
                  {preview.data.per_user.slice(0, 20).map((u) => (
                    <tr key={u.user_id} className="border-t">
                      <td className="p-2">{u.name}</td>
                      <td className="p-2 text-right tabular-nums text-muted-foreground">{u.violation_count} violation{u.violation_count === 1 ? "" : "s"}</td>
                      <td className={`p-2 text-right tabular-nums font-mono ${u.points_delta >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{u.points_delta >= 0 ? "+" : ""}{u.points_delta}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </details>
          )}
        </div>
      )}
      {preview.err && <p className="text-xs text-rose-700" data-testid="rules-preview-err">{preview.err}</p>}
    </div>
  );
}
